/* eslint-disable unused-imports/no-unused-vars */
import { useState, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import { addClickRecord, getCaseId, getClientId } from "../../Utils/helper";
import { api_without_cancellation } from "../../api/api";
import CounselFileInformationFields from "../DefendantPage/DefandantMain/modals/fields/counsel/CounselFileInformationFields";
import CounselFirmFields from "../DefendantPage/DefandantMain/modals/fields/counsel/CounselFirmFields";
import CounselAttorneyFields from "../DefendantPage/DefandantMain/modals/fields/counsel/CounselAttorneyFields";
import CounselSupervisingAttorneyFields from "../DefendantPage/DefandantMain/modals/fields/counsel/CounselSupervisingAttorneyFields";

// ── Tab grid (same pattern as AddDefendantModal) ───────────────────────────
const TabGrid = ({ tabs, selectedId, onSelect }) => (
  <div className="w-100 m-t-5 p-l-5 p-r-5" style={{ minHeight: "25px" }}>
    <div
      className="overflow-scroll invisible-scrollbar"
      style={{
        display: "grid",
        gridTemplateRows: `repeat(${Math.ceil((tabs?.length ?? 0) / 4)}, 1fr)`,
        gridAutoFlow: "column",
        gap: "5px",
      }}
    >
      {tabs?.map((item) => {
        const isSelected = selectedId === item.id;
        return (
          <button
            key={item.id}
            type="button"
            className={`event-type-button btn-hover-event-litigation-act height-25 ${
              isSelected ? "selected" : "unselected"
            }`}
            onClick={() => onSelect(item.id)}
          >
            <span className="text-nowrap">{item.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

// ── Routes the active sub-tab to the right fieldset ────────────────────────
const FormSection = ({ subTab, entityId, statesAbrs }) => {
  if (subTab === "fileInformation")
    return <CounselFileInformationFields entityId={entityId} pageName={null} />;
  if (subTab === "firm") return <CounselFirmFields entityId={entityId} statesData={statesAbrs} />;
  if (subTab === "attorney")
    return <CounselAttorneyFields entityId={entityId} statesData={statesAbrs} />;
  if (subTab === "supervisingAttorney")
    return <CounselSupervisingAttorneyFields entityId={entityId} statesData={statesAbrs} />;
  return null;
};

const SUB_TABS = [
  { id: "fileInformation", label: "File Information" },
  { id: "firm", label: "Firm" },
  { id: "attorney", label: "Attorney" },
  { id: "supervisingAttorney", label: "Supervising Attorney" },
];

// Single fixed entity for now. When multi-counsel support is added,
// swap this for `uid()` + an entities array like AddDefendantModal does.
// const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const FIXED_ENTITY_ID = 1;

const AddCounselModelCounselPage = ({ handleClose, show, refetch }) => {
  const client = getClientId();
  const currentCase = getCaseId();

  const methods = useForm({
    defaultValues: {
      counsel: {
        [FIXED_ENTITY_ID]: {},
      },
    },
  });
  const { handleSubmit, reset, watch, setValue } = methods;
  const [subTab, setSubTab] = useState(SUB_TABS[0].id);
  const [loading, setLoading] = useState(false);
  const [statesAbrs, setStatesAbrs] = useState([]);

  // const [entities, setEntities] = useState([{ id: FIXED_ENTITY_ID }]);
  // const [activeEntityId, setActiveEntityId] = useState(FIXED_ENTITY_ID);

  const fetchStatesData = async () => {
    try {
      const response = await api_without_cancellation.get("/api/states/");
      if (response.status === 200) setStatesAbrs(response.data);
    } catch (error) {
      console.error("Failed to fetch states:", error);
    }
  };

  useEffect(() => {
    fetchStatesData();
  }, []);

  const isNonEmpty = (obj) =>
    obj &&
    Object.values(obj).some((v) => {
      if (v === null || v === undefined) return false;
      if (typeof v === "string") return v.trim() !== "";
      if (typeof v === "object") return Object.values(v).some(Boolean);
      return true;
    });

  const [partiesData, setPartiesData] = useState(null);
  const combineParties = (partiesData) => {
    const combinedList = [];

    // Add clients
    if (partiesData?.clients) {
      partiesData?.clients?.forEach((client) => {
        combinedList.push({
          id: client.id,
          type: "Client",
          name: `${client?.first_name || ""} ${client?.last_name || ""} - Client`.trim(),
        });
      });
    }

    // Add defendants
    if (partiesData?.defendants) {
      partiesData?.defendants?.forEach((defendant) => {
        combinedList.push({
          id: defendant.id,
          type: "Defendant",
          name: `${
            defendant?.defendantType?.id === 1
              ? `${defendant?.first_name || ""} ${defendant?.last_name || ""}`.trim()
              : defendant?.entity_name || ""
          } - Defendant`,
        });
      });
    }
    // Add other parties
    if (partiesData?.other_parties) {
      partiesData?.other_parties?.forEach((party) => {
        combinedList.push({
          id: party.id,
          type: "OtherParty",
          name: `${party?.party_first_name || party?.first_name || ""} ${party?.party_last_name || party?.last_name || ""} - OtherParty`.trim(),
        });
      });
    }

    if (partiesData?.witnesses) {
      partiesData?.witnesses?.forEach((witness) => {
        combinedList.push({
          id: witness.id,
          type: "Witness",
          name: `${witness?.witness_first_name || ""} ${witness?.witness_last_name || ""} - Witness`.trim(),
        });
      });
    }
    return combinedList;
  };
  const fetchPartiesData = async () => {
    try {
      const response = await api_without_cancellation.get(
        `/api/litigation-page/defendants-and-other-parties/${getClientId()}/${getCaseId()}/`
      );
      setPartiesData(combineParties(response.data));
    } catch (err) {
      console.error("error", err);
    }
  };

  useEffect(() => {
    fetchPartiesData();
  }, []);
  const getEntityIcon = (type) => {
    switch (type) {
      case "Client":
        return "ic-client";
      case "Defendant":
        return "ic-defendants";
      case "OtherParty":
        return "ic-parties";
      case "Witness":
        return "ic-witnesses";
      default:
        return "ic-client";
    }
  };
  const [isEntityDropdownOpen, setIsEntityDropdownOpen] = useState(false);
  const [selectedEntityLabel, setSelectedEntityLabel] = useState("Select Entity");
  const [selectedEntityIcon, setSelectedEntityIcon] = useState(null);
  const entityDropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (entityDropdownRef.current && !entityDropdownRef.current.contains(event.target)) {
        setIsEntityDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const handleEntityDropdownToggle = () => {
    setIsEntityDropdownOpen((prev) => !prev);
    addClickRecord(999, 64);
  };
  const onSubmit = async (data) => {
    const { counsel: counselMap = {} } = data;
    const counsels = Object.values(counselMap).filter(isNonEmpty);
    const payload = {
      client_id: getClientId(),
      counsels,
    };

    if (data.entity_type === "Defendant") {
      payload.defendant_id = data.counsel_entity;
    } else if (data.entity_type === "OtherParty") {
      payload.party_id = data.counsel_entity;
    } else if (data.entity_type === "Witness") {
      payload.party_id = data.counsel_entity;
    }

    delete payload.entity_type;
    delete payload.counsel_entity;
    addClickRecord(999, 64);
    try {
      setLoading(true);
      await api_without_cancellation.post(
        `/api/defendants/create_update_counsels/${currentCase}/`,
        payload
      );
      addClickRecord(999, 2);
      reset();
      handleClose();
      refetch();
    } catch (err) {
      console.error("Counsel save failed:", err.response?.data ?? err.message);
    } finally {
      setLoading(false);
    }
  };
  const handleEntitySelection = (e, value, label, name, iconClass, _entityType) => {
    e.stopPropagation();
    setValue("counsel_entity", value);
    setValue("entity_type", label);
    setSelectedEntityLabel(name);
    setSelectedEntityIcon(iconClass);
    setIsEntityDropdownOpen(false);
  };
  return (
    <FormProvider {...methods}>
      <Modal
        show={show}
        onHide={() => {
          addClickRecord(999, 2);
          handleClose();
        }}
        dialogClassName="modal-dialog-centered modal-800p"
      >
        <div
          className="text-center height-25 p-0 bg-primary modal-header"
          style={{ border: "none" }}
        >
          <div
            className="mx-auto height-25 text-white d-flex align-items-center"
            style={{ fontSize: 14, fontWeight: 600 }}
          >
            Add Counsel
          </div>
        </div>

        <Modal.Body style={{ padding: 0 }}>
          <form id="addcounsel_form" onSubmit={handleSubmit(onSubmit)}>
            <>
              <div className="p-l-5 p-r-5 custom-margin-bottom custom-margin-top">
                <div className="dropdown-container" ref={entityDropdownRef}>
                  <div
                    className="height-25 p-0 rounded-0 form-control form-select d-flex align-items-center custom-select-state-entity"
                    onClick={handleEntityDropdownToggle}
                    style={{ padding: "0px", cursor: "pointer" }}
                  >
                    {selectedEntityIcon && (
                      <i className={`ic ic-19 ${selectedEntityIcon} m-r-5 m-l-5`} />
                    )}
                    <span style={{ padding: !selectedEntityIcon ? "5px" : "" }}>
                      {selectedEntityLabel}
                    </span>
                    {isEntityDropdownOpen && (
                      <ul
                        className="dropdown-list"
                        style={{
                          marginTop: "0px",
                          top: "24px",
                          border: "1px solid #ced4ea",
                          left: "0px",
                        }}
                      >
                        {partiesData?.map((party, index) => {
                          const icon = getEntityIcon(party.type);
                          return (
                            <li
                              style={{ padding: "5px" }}
                              key={index}
                              onClick={(e) =>
                                handleEntitySelection(e, party.id, party.type, party.name, icon)
                              }
                            >
                              <i className={`ic ic-19 ${icon} m-r-5`} />
                              {party.name}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </>
            <div
              style={{ minHeight: "300px" }}
              className="custom-tab m-t-5 m-b-5 defendant-modal-content"
            >
              <TabGrid tabs={SUB_TABS} selectedId={subTab} onSelect={setSubTab} />

              {/* When multi-counsel comes in, render the third-row TabGrid here
                  with onAdd/onDelete wired to entities state — see AddDefendantModal. */}

              <FormSection
                key={`${subTab}-${FIXED_ENTITY_ID}`}
                subTab={subTab}
                entityId={FIXED_ENTITY_ID}
                statesAbrs={statesAbrs}
              />

              <div
                style={{ position: "absolute", bottom: "0px" }}
                className="d-flex w-100 justify-content-between custom-margin-top p-b-5 p-l-5 p-r-5"
              >
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    addClickRecord(999, 2);
                    handleClose();
                  }}
                  className="height-25"
                  style={{ padding: "0px 12px" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="success"
                  type="submit"
                  className="m-l-5 bg-success height-25"
                  style={{ padding: "0px 12px" }}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save & Close"}
                </Button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </FormProvider>
  );
};

export default AddCounselModelCounselPage;
